import React from "react";
import { motion } from "framer-motion";
import { AlertCircle, Clock, TrendingDown } from "lucide-react";

const CHALLENGES = [
  {
    icon: AlertCircle,
    iconColor: "text-red-400",
    iconBg: "bg-red-500/10 border-red-500/20",
    glowFrom: "from-red-500/10",
    title: "Fragmented Systems",
    desc: "Marketing, sales, and accounts run on separate databases. No single source of truth for leads or payment statuses.",
    bulletPoints: [
      "Sales teams work in silos from developers",
      "Manual data transfers create reconciliation errors",
    ],
  },
  {
    icon: Clock,
    iconColor: "text-orange-400",
    iconBg: "bg-orange-500/10 border-orange-500/20",
    glowFrom: "from-orange-500/10",
    title: "Slow Lead Response",
    desc: "Incoming leads wait hours for assignment. By then, prospective buyers have moved on to competitors.",
    bulletPoints: [
      "No automated WhatsApp/SMS sequences",
      "Leads go cold before sales representatives connect",
    ],
  },
  {
    icon: TrendingDown,
    iconColor: "text-yellow-400",
    iconBg: "bg-yellow-500/10 border-yellow-500/20",
    glowFrom: "from-yellow-500/10",
    title: "Revenue Leakage",
    desc: "Milestone payments are missed because ledger updates and reminder emails are managed manually via spreadsheets.",
    bulletPoints: [
      "Reminders are sent weeks after dues elapse",
      "Zero pipeline visibility on blocked/booked unit status",
    ],
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

export function ChallengeSection() {
  return (
    <section
      className="relative z-10 py-16 md:py-24"
      aria-label="Why real estate teams need Clozdl.ai"
    >
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-widest uppercase text-[#4B73FF] font-semibold mb-3">
            The Challenge
          </p>
          <h2 className="text-[2.1rem] font-bold leading-tight text-white tracking-tight sm:text-[2.6rem] lg:text-[3rem]">
            Why Real Estate Teams <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-b from-white to-[#4B73FF]">
              Need Clozdl.ai
            </span>
          </h2>
        </motion.div>

        {/* Challenges Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8"
        >
          {CHALLENGES.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={idx}
                variants={cardVariants}
                className="bg-gradient-to-br from-white/5 to-white/[0.01] backdrop-blur-md border border-white/10 rounded-2xl p-8 hover:border-[#4B73FF]/30 hover:-translate-y-2 transition-all duration-300 relative overflow-hidden group shadow-lg flex flex-col"
              >
                {/* Background glow hover */}
                <div
                  className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${item.glowFrom} to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`}
                />

                {/* Icon wrapper */}
                <div
                  className={`w-14 h-14 rounded-2xl border ${item.iconBg} flex items-center justify-center mb-6`}
                >
                  <Icon className={`w-7 h-7 ${item.iconColor}`} />
                </div>

                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-6 flex-1">{item.desc}</p>

                <div className="space-y-3 border-t border-white/5 pt-4">
                  {item.bulletPoints.map((bullet, bIdx) => (
                    <div key={bIdx} className="flex items-start gap-2.5 text-xs text-white/40">
                      <svg
                        className="w-4 h-4 text-red-500/50 mt-0.5 shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
